import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { combineLatest, withLatestFrom } from 'rxjs';

import {
  Chart,
  type ChartData,
  type ChartOptions,
  type TooltipItem,
} from 'chart.js/auto';
import { DatePipe } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';

import { selectCurrentSite } from '../../../features/site/site.selectors';
import { selectUser } from '../../../features/user/user.selectors';
import { selectCurrentReferenceBatchId } from '../../../features/batch/batch.selectors';
import { selectCurrentTrackerElevation } from '../../../features/tracker/tracker.selectors';

import { Color } from '../../../enums/color';
import { TrackerElevation, TrackerElevationBatchData } from '../../../models/tracker';
import { Site } from '../../../models/site';
import { User } from '../../../models/user';

type XY = { x: number; y: number };

type ExternalTooltipContext = {
  chart: Chart;
  tooltip: {
    opacity: number;
    caretX: number;
    caretY: number;
    dataPoints?: TooltipItem<'line'>[];
  };
};

@Component({
    selector: 'elevation-chart',
    template: `
    <div #host class="host">
      <canvas #canvas></canvas>
      <div #tooltip class="tooltip"></div>
    </div>
  `,
    styles: [`
    :host { display:block; width:100%; height:100%; }
    .host { position: relative; width:100%; height:100%; }
    canvas { width:100% !important; height:100% !important; display:block; }
    .tooltip {
      position: absolute;
      display: none;
      background: #333;
      color: #fff;
      padding: 8px 10px;
      border-radius: 6px;
      pointer-events: none;
      font-size: 14px;
      white-space: nowrap;
    }
  `],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ElevationChartComponent implements AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('canvas') canvasRef?: ElementRef<HTMLCanvasElement>;
  @ViewChild('host') hostRef?: ElementRef<HTMLElement>;
  @ViewChild('tooltip') tooltipRef?: ElementRef<HTMLDivElement>;

  private chart?: Chart;

  private datePipe = new DatePipe(this.translocoService.getActiveLang());

  private site?: Site | null;
  private elevation?: TrackerElevation | null;
  private user?: User | null;
  private currentBatchId?: string | null;

  constructor(
    private store: Store,
    private translocoService: TranslocoService,
    private zone: NgZone
  ) {
    combineLatest([
      this.store.select(selectCurrentTrackerElevation),
      this.store.select(selectCurrentReferenceBatchId),
    ])
      .pipe(
        withLatestFrom(
          this.store.select(selectCurrentSite),
          this.store.select(selectUser),
        ),
        takeUntilDestroyed()
      )
      .subscribe(([
                    [elevation, currentBatchId],
                    site,
                    user
                  ]) => {
        this.site = site;
        this.elevation = elevation;
        this.user = user;
        this.currentBatchId = currentBatchId;

        // update only if chart exists; otherwise ngAfterViewInit will create it once
        if (this.chart) {
          this.updateChart();
        }
      });
  }

  ngAfterViewInit(): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Create ONCE
    this.chart = new Chart(ctx, {
      type: 'line',
      data: this.buildChartData(),
      options: this.buildOptions()
    });

    // When inserted via ngIf, layout can settle over 1-2 frames
    this.resizeBurst();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    // kept for symmetry; not required if you only use store
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  // ----------------------------
  // Chart build / update
  // ----------------------------

  private buildChartData(): ChartData {
    const elevation = this.elevation;
    const site = this.site;
    const user = this.user;
    const currentBatchId = this.currentBatchId;

    if (!elevation || !site || !user || !elevation.data?.length) {
      return { datasets: [] };
    }

    const dateFormat = user.languageCode === 'fr' ? 'dd/MM/yyyy' : 'MM/dd/yyyy';

    // plus ancien = dernier élément chez toi
    const older = elevation.data[elevation.data.length - 1];
    const olderDate = new Date(older.dateAcquired).getTime();

    const defaultColor = '#AAAAAA';

    const points: XY[] = new Array(elevation.data.length);
    const pointColors: string[] = new Array(elevation.data.length);
    const labels: string[] = new Array(elevation.data.length);

    for (let i = 0; i < elevation.data.length; i++) {
      const ebd = elevation.data[i] as TrackerElevationBatchData;
      const t = new Date(ebd.dateAcquired).getTime();
      const nbDays = Math.floor(Math.abs((t - olderDate) / (1000 * 60 * 60 * 24)));

      points[i] = { x: nbDays, y: Number(ebd.altitude ?? 0) };

      if (ebd.batch === site.lastBatch) pointColors[i] = Color.PRIMARY as any;
      else if (ebd.batch === currentBatchId) pointColors[i] = Color.ACCENT as any;
      else pointColors[i] = defaultColor;

      labels[i] = this.datePipe.transform(new Date(ebd.dateAcquired), dateFormat) ?? '';
    }

    return {
      labels,
      datasets: [{
        data: points,
        parsing: false as const, // data = {x,y}
        normalized: true,
        borderColor: defaultColor,
        pointBackgroundColor: pointColors,
        pointBorderColor: pointColors,
        pointRadius: 5,
        pointHoverRadius: 6,
        tension: 0, // ligne droite (mets 0.2 si tu veux lisser)
      }]
    };
  }

  private buildOptions(): ChartOptions {
    // IMPORTANT: external tooltip must use "function" to access chart context,
    // but we still need access to this.tooltipRef => we close over "self".
    const self = this;

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: { duration: 0 },
      plugins: {
        legend: { display: false },
        tooltip: {
          enabled: false,
          external(context) {
            self.renderExternalTooltip(context);
          }
        }
      },
      interaction: { mode: 'nearest', intersect: true },
      scales: {
        x: {
          type: 'linear',
          beginAtZero: true,
          ticks: { display: false }
        },
        y: {
          type: 'linear',
          beginAtZero: true
        }
      }
    };
  }

  private updateChart(): void {
    if (!this.chart) return;

    const newData = this.buildChartData();

    // Update in place
    this.chart.data.labels = newData.labels;

    const ds = this.chart.data.datasets[0] as any;
    const newDs = newData.datasets?.[0] as any;

    if (!newDs) {
      this.chart.data.datasets = [];
      this.chart.update('none');
      return;
    }

    ds.data = newDs.data;
    ds.borderColor = newDs.borderColor;
    ds.pointBackgroundColor = newDs.pointBackgroundColor;
    ds.pointBorderColor = newDs.pointBorderColor;
    ds.pointRadius = newDs.pointRadius;
    ds.pointHoverRadius = newDs.pointHoverRadius;
    ds.tension = newDs.tension;
    ds.parsing = false as const;
    ds.normalized = true;

    this.chart.update('none');
  }

  // ----------------------------
  // Tooltip external (no global id, correct positioning)
  // ----------------------------

  private renderExternalTooltip(context: ExternalTooltipContext): void {
    const tooltipEl = this.tooltipRef?.nativeElement;
    const hostEl = this.hostRef?.nativeElement;
    if (!tooltipEl || !hostEl) return;

    const tooltipModel = context.tooltip;
    if (!tooltipModel || tooltipModel.opacity === 0) {
      tooltipEl.style.display = 'none';
      return;
    }

    const dp = tooltipModel.dataPoints?.[0];
    if (!dp) {
      tooltipEl.style.display = 'none';
      return;
    }

    // --------- Content ---------
    const label = context.chart.data.labels?.[dp.dataIndex] ?? '';
    const value = dp.formattedValue ?? '';
    tooltipEl.innerHTML = `<strong>${label}</strong><br>${value} m`;

    // --------- Position (relative to host) ---------
    // Make it measurable
    tooltipEl.style.display = 'block';
    tooltipEl.style.visibility = 'hidden';
    tooltipEl.style.transform = 'none'; // important: we position with pixels, not CSS transform

    const hostRect = hostEl.getBoundingClientRect();
    const canvasRect = context.chart.canvas.getBoundingClientRect();

    // caretX/Y are relative to canvas, convert to host coordinates
    const caretX = (canvasRect.left - hostRect.left) + tooltipModel.caretX;
    const caretY = (canvasRect.top - hostRect.top) + tooltipModel.caretY;

    // Measure tooltip
    const w = tooltipEl.offsetWidth;
    const h = tooltipEl.offsetHeight;

    const padding = 8;   // keep inside host
    const offset = 12;   // gap from point

    // Default: centered above the point
    let left = caretX - w / 2;
    let top = caretY - h - offset;

    // Clamp X within host
    left = Math.max(padding, Math.min(left, hostRect.width - w - padding));

    // If it goes above, place below the point
    if (top < padding) {
      top = caretY + offset;
    }

    // Clamp Y within host
    top = Math.max(padding, Math.min(top, hostRect.height - h - padding));

    tooltipEl.style.left = `${left}px`;
    tooltipEl.style.top = `${top}px`;
    tooltipEl.style.visibility = 'visible';
  }

  private resizeBurst(): void {
    if (!this.chart) return;

    const host = this.hostRef?.nativeElement;
    const canResize = () => {
      if (!host) return true;
      const r = host.getBoundingClientRect();
      return r.width > 0 && r.height > 0;
    };

    requestAnimationFrame(() => {
      if (!this.chart || !canResize()) return;
      this.chart.resize();

      requestAnimationFrame(() => {
        if (!this.chart || !canResize()) return;
        this.chart.resize();
      });
    });
  }

  // ----------------------------
  // Export
  // ----------------------------

  canvasToImage(filename: string): void {
    const canvas = this.canvasRef?.nativeElement;
    if (!canvas) return;

    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename.replace(/ /g, '_');
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }
}
